import { BedrockRuntimeClient, InvokeModelCommand, InvokeModelWithResponseStreamCommand, ConverseCommand, ConverseStreamCommand } from '@aws-sdk/client-bedrock-runtime';
import { BedrockClient, ListFoundationModelsCommand, ListInferenceProfilesCommand } from '@aws-sdk/client-bedrock';
import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from '@aws-sdk/client-cognito-identity';
import { STSClient, AssumeRoleCommand, AssumeRoleWithWebIdentityCommand } from '@aws-sdk/client-sts';
import authService from './authService';

class BedrockService {
  constructor() {
    this.identityPoolId = null;
    this.region = null;
    this.userPoolId = null;
    this.userRoleArn = null; // User's role to assume
    this.cacheSize = 2; // Number of recent assistant messages to cache
    this.activeAbortControllers = new Set(); // Track active requests
  }

  /**
   * Abort all active requests
   */
  abortAll() {
    console.log(`Aborting ${this.activeAbortControllers.size} active Bedrock requests`);
    for (const controller of this.activeAbortControllers) {
      try {
        controller.abort();
      } catch (err) {
        console.error('Error aborting controller:', err);
      }
    }
    this.activeAbortControllers.clear();
  }

  /**
   * Create and register an AbortController for a request
   */
  createAbortController() {
    const controller = new AbortController();
    this.activeAbortControllers.add(controller);
    return controller;
  }

  /**
   * Remove an AbortController from tracking (when request completes)
   */
  removeAbortController(controller) {
    this.activeAbortControllers.delete(controller);
  }

  /**
   * Get Cognito Identity credentials
   */
  async getCognitoCredentials() {
    if (!this.identityPoolId) {
      throw new Error('Bedrock service not configured. Call configure() first.');
    }

    try {
      // Get the ID token from Cognito User Pool
      const session = await authService.getCurrentSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No ID token available. Please sign in.');
      }

      // Get Cognito Identity credentials
      const cognitoIdentity = new CognitoIdentityClient({
        region: this.region,
      });

      // Get Identity ID
      const getIdCommand = new GetIdCommand({
        IdentityPoolId: this.identityPoolId,
        Logins: {
          [`cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`]: idToken,
        },
      });

      const identityResponse = await cognitoIdentity.send(getIdCommand);
      const identityId = identityResponse.IdentityId;

      // Get credentials for the identity
      const getCredentialsCommand = new GetCredentialsForIdentityCommand({
        IdentityId: identityId,
        Logins: {
          [`cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`]: idToken,
        },
      });

      const credentialsResponse = await cognitoIdentity.send(getCredentialsCommand);

      return {
        accessKeyId: credentialsResponse.Credentials.AccessKeyId,
        secretAccessKey: credentialsResponse.Credentials.SecretKey,
        sessionToken: credentialsResponse.Credentials.SessionToken,
      };
    } catch (error) {
      console.error('Error getting Cognito credentials:', error);
      throw error;
    }
  }

  /**
   * Configure the Bedrock service
   */
  configure(identityPoolId, region, userPoolId, authenticatedRoleArn) {
    this.identityPoolId = identityPoolId;
    this.region = region;
    this.userPoolId = userPoolId;
    // authenticatedRoleArn not used - kept for API compatibility
  }

  /**
   * Set the user's role ARN to assume
   */
  setUserRole(userRoleArn) {
    this.userRoleArn = userRoleArn;
  }

  /**
   * Set tool specifications for use in conversations
   */
  setToolSpecs(toolSpecs) {
    this.toolSpecs = toolSpecs;
  }

  /**
   * Get current tool specifications
   */
  getToolSpecs() {
    return this.toolSpecs || [];
  }

  /**
   * Apply prompt caching to conversation messages
   * Adds cache control to the last N assistant messages to enable caching
   * @param {Array} messages - The conversation messages array
   * @returns {Array} - Messages with cache controls applied
   */
  setMessageCaches(messages) {
    // Create a deep copy to avoid mutating the original
    const messagesCopy = JSON.parse(JSON.stringify(messages));
    
    // First, remove any existing cache controls from all messages
    for (const msg of messagesCopy) {
      if (msg.content && Array.isArray(msg.content)) {
        for (const contentBlock of msg.content) {
          if (contentBlock && typeof contentBlock === 'object') {
            delete contentBlock.cacheControl;
          }
        }
      }
    }

    // Find indices of all assistant messages
    const assistantIndices = [];
    for (let i = 0; i < messagesCopy.length; i++) {
      if (messagesCopy[i].role === 'assistant') {
        assistantIndices.push(i);
      }
    }

    // Add cache control to the last N assistant messages
    if (assistantIndices.length > 0) {
      // Only cache if we have enough assistant messages, or cache all if we have fewer
      const numToCache = Math.min(this.cacheSize, assistantIndices.length);
      const cacheIndices = assistantIndices.slice(-numToCache);

      for (const index of cacheIndices) {
        const message = messagesCopy[index];
        if (message.content && Array.isArray(message.content) && message.content.length > 0) {
          // Add cache control to the last content block of this message
          const lastContentIndex = message.content.length - 1;
          const lastContent = message.content[lastContentIndex];
          
          if (lastContent && typeof lastContent === 'object') {
            lastContent.cacheControl = {
              type: 'default'
            };
          }
        }
      }
    }

    return messagesCopy;
  }

  /**
   * Perform double role assumption:
   * 1. Get Cognito Identity credentials
   * 2. Use them to assume the user's role
   */
  async getAssumedCredentials() {
    if (!this.userRoleArn) {
      throw new Error('User role ARN not set. Call setUserRole() first.');
    }

    try {
      // Step 1: Get Cognito Identity credentials
      const cognitoCredentials = await this.getCognitoCredentials();

      // Step 2: Assume the user's role using Cognito credentials
      const sts = new STSClient({
        region: this.region,
        credentials: {
          accessKeyId: cognitoCredentials.accessKeyId,
          secretAccessKey: cognitoCredentials.secretAccessKey,
          sessionToken: cognitoCredentials.sessionToken,
        },
      });

      const assumeRoleCommand = new AssumeRoleCommand({
        RoleArn: this.userRoleArn,
        RoleSessionName: 'BedrockChatSession',
        DurationSeconds: 3600,
      });

      const assumedRole = await sts.send(assumeRoleCommand);

      return {
        accessKeyId: assumedRole.Credentials.AccessKeyId,
        secretAccessKey: assumedRole.Credentials.SecretAccessKey,
        sessionToken: assumedRole.Credentials.SessionToken,
      };
    } catch (error) {
      console.error('Error performing role assumption:', error);
      throw error;
    }
  }

  /**
   * List available Bedrock inference profiles and foundation models
   */
  async listModels() {
    try {
      // Always use assumed credentials
      const credentials = await this.getAssumedCredentials();

      // Create Bedrock client with the credentials
      const bedrock = new BedrockClient({
        region: this.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      const allModels = [];

      try {
        // First, try to list inference profiles (these are preferred for some models)
        const inferenceProfileCommand = new ListInferenceProfilesCommand({});
        const inferenceProfileResponse = await bedrock.send(inferenceProfileCommand);

        // Add inference profiles to the list
        if (inferenceProfileResponse.inferenceProfileSummaries) {
          const inferenceProfiles = inferenceProfileResponse.inferenceProfileSummaries
            .filter(profile => profile.inferenceProfileArn && profile.inferenceProfileName)
            .map(profile => ({
              id: profile.inferenceProfileArn,
              name: profile.inferenceProfileName,
              provider: 'Inference Profile',
              type: 'inference_profile'
            }));
          allModels.push(...inferenceProfiles);
        }
      } catch (inferenceError) {
        console.warn('Could not list inference profiles:', inferenceError);
        // Continue to list foundation models as fallback
      }

      try {
        // Also list foundation models as fallback options
        const foundationModelCommand = new ListFoundationModelsCommand({
          byOutputModality: 'TEXT',
        });
        const foundationModelResponse = await bedrock.send(foundationModelCommand);

        // Add foundation models to the list
        if (foundationModelResponse.modelSummaries) {
          const foundationModels = foundationModelResponse.modelSummaries
            .filter(model => model.modelId && model.modelName)
            .map(model => {
              let modelId = model.modelId;
              
              // For Anthropic 4.x models, prepend "global." to match inference profile format
              if (model.modelId && model.modelId.includes('anthropic') && model.modelId.includes('-4')) {
                modelId = `global.${model.modelId}`;
              }
              
              return {
                id: modelId,
                name: model.modelName,
                provider: model.providerName || 'Foundation Model',
                type: 'foundation_model'
              };
            });
          allModels.push(...foundationModels);
        }
      } catch (foundationError) {
        console.warn('Could not list foundation models:', foundationError);
      }

      // Sort by name, with inference profiles first
      return allModels.sort((a, b) => {
        // Prioritize inference profiles over foundation models
        if (a.type !== b.type) {
          return a.type === 'inference_profile' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
    } catch (error) {
      console.error('Error listing Bedrock models:', error);
      throw error;
    }
  }

  /**
   * Send a message to Bedrock and get a response
   * Always uses triple role assumption
   */
  async sendMessage(message, modelId = 'anthropic.claude-3-sonnet-20240229-v1:0', conversationHistory = []) {
    try {
      // Always use assumed credentials
      const credentials = await this.getAssumedCredentials();

      // Create Bedrock Runtime client with the credentials
      const bedrockRuntime = new BedrockRuntimeClient({
        region: this.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      // Build the messages array from conversation history
      // If message is null, we're continuing with tool results already in history
      const messages = message !== null ? [
        ...conversationHistory,
        {
          role: 'user',
          content: modelId.includes('anthropic') ? [{ type: 'text', text: message }] : [{ text: message }],
        },
      ] : conversationHistory;

      // Prepare the request body based on the model
      const requestBody = {
        messages: messages,
      };

      // Add max_tokens and anthropic_version for Anthropic models
      if (modelId.includes('anthropic')) {
        requestBody.max_tokens = 4096;
        requestBody.anthropic_version = 'bedrock-2023-05-31';
      }

      // Invoke the model
      const command = new InvokeModelCommand({
        modelId: modelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(requestBody),
      });

      const response = await bedrockRuntime.send(command);

      // Parse the response
      const responseBody = JSON.parse(new TextDecoder().decode(response.body));
      
      console.log('Bedrock response:', responseBody);

      // Extract text from response - handle different possible formats
      let responseText = '';
      if (responseBody.content && Array.isArray(responseBody.content) && responseBody.content.length > 0) {
        responseText = responseBody.content[0].text || '';
      } else if (responseBody.completion) {
        // Some models use completion field
        responseText = responseBody.completion;
      } else {
        console.error('Unexpected response format:', responseBody);
        responseText = 'Error: Unexpected response format';
      }

      return {
        response: responseText,
        conversationHistory: [
          ...conversationHistory,
          {
            role: 'user',
            content: [{ text: message }],
          },
          {
            role: 'assistant',
            content: responseBody.content || [{ text: responseText }],
          },
        ],
      };
    } catch (error) {
      console.error('Error sending message to Bedrock:', error);
      throw error;
    }
  }

  /**
   * Send a message with MCP gateway tool support using Converse API
   * Handles multi-turn conversations with tool use
   * Supports prompt caching for system prompt, tools, and conversation history
   * Always uses triple role assumption
   */
  async sendMessageWithTools(message, modelId = 'anthropic.claude-3-sonnet-20240229-v1:0', conversationHistory = [], systemPrompt = null) {
    try {
      // Always use assumed credentials
      const credentials = await this.getAssumedCredentials();

      // Create Bedrock Runtime client with the credentials
      const bedrockRuntime = new BedrockRuntimeClient({
        region: this.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      // Build the messages array from conversation history
      // If message is null, we're continuing with tool results already in history
      let messages = message !== null ? [
        ...conversationHistory,
        {
          role: 'user',
          content: [{ text: message }],
        },
      ] : conversationHistory;

      // Apply message caching to conversation history
      messages = this.setMessageCaches(messages);

      // Prepare command parameters
      const commandParams = {
        modelId: modelId,
        messages: messages,
      };

      // Add system prompt with cache control if provided
      if (systemPrompt) {
        console.log('Adding system prompt to Converse API:', systemPrompt);
        commandParams.system = [
          {
            text: systemPrompt,
            cacheControl: { type: 'default' }, // Enable caching for system prompt
          },
        ];
      } else {
        console.warn('No system prompt provided to Converse API');
      }

      // Only include toolConfig if tool specs are set
      if (this.toolSpecs && this.toolSpecs.length > 0) {
        // Add cache control to tool specs (tools are relatively static)
        const cachedToolSpecs = this.toolSpecs.map((toolSpec, index) => {
          // Add cache control to the last tool spec to cache all tools
          if (index === this.toolSpecs.length - 1) {
            return {
              ...toolSpec,
              cacheControl: { type: 'default' },
            };
          }
          return toolSpec;
        });
        
        commandParams.toolConfig = {
          tools: cachedToolSpecs,
        };
      }

      // Use Converse API for tool support
      const command = new ConverseCommand(commandParams);

      const response = await bedrockRuntime.send(command);

      console.log('Bedrock Converse response:', response);

      // Extract the response content
      const output = response.output;
      let assistantContent = [];
      let responseText = '';

      if (output.message) {
        assistantContent = output.message.content;
        // Extract text from content blocks
        for (const block of assistantContent) {
          if (block.text) {
            responseText += block.text;
          }
        }
      }

      // Check if the model wants to use tools
      const stopReason = response.stopReason;
      
      // Build conversation history based on whether we added a new user message
      const newHistory = message !== null ? [
        ...conversationHistory,
        {
          role: 'user',
          content: [{ text: message }],
        },
        {
          role: 'assistant',
          content: assistantContent,
        },
      ] : [
        ...conversationHistory,
        {
          role: 'assistant',
          content: assistantContent,
        },
      ];

      return {
        response: responseText,
        stopReason: stopReason,
        toolUse: stopReason === 'tool_use' ? assistantContent.filter(block => block.toolUse) : null,
        conversationHistory: newHistory,
        usage: response.usage || null, // Include token usage stats
      };
    } catch (error) {
      console.error('Error sending message with tools to Bedrock:', error);
      throw error;
    }
  }

  /**
   * Send a message with MCP gateway tool support and streaming using Converse API
   * Supports prompt caching for system prompt, tools, and conversation history
   * Always uses triple role assumption
   */
  async sendMessageWithToolsStream(message, modelId = 'anthropic.claude-3-sonnet-20240229-v1:0', conversationHistory = [], onChunk, onToolUse, systemPrompt = null) {
    try {
      // Always use assumed credentials
      const credentials = await this.getAssumedCredentials();

      // Create Bedrock Runtime client with the credentials
      const bedrockRuntime = new BedrockRuntimeClient({
        region: this.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      // Build the messages array from conversation history
      // If message is null, we're continuing with tool results already in history
      let messages = message !== null ? [
        ...conversationHistory,
        {
          role: 'user',
          content: [{ text: message }],
        },
      ] : conversationHistory;

      // Apply message caching to conversation history
      messages = this.setMessageCaches(messages);

      // Prepare command parameters
      const commandParams = {
        modelId: modelId,
        messages: messages,
      };

      // Add system prompt with cache control if provided
      if (systemPrompt) {
        commandParams.system = [
          {
            text: systemPrompt,
            cacheControl: { type: 'default' }, // Enable caching for system prompt
          },
        ];
      }

      // Only include toolConfig if tool specs are set
      if (this.toolSpecs && this.toolSpecs.length > 0) {
        // Add cache control to tool specs (tools are relatively static)
        const cachedToolSpecs = this.toolSpecs.map((toolSpec, index) => {
          // Add cache control to the last tool spec to cache all tools
          if (index === this.toolSpecs.length - 1) {
            return {
              ...toolSpec,
              cacheControl: { type: 'default' },
            };
          }
          return toolSpec;
        });
        
        commandParams.toolConfig = {
          tools: cachedToolSpecs,
        };
      }

      // Use Converse Stream API for tool support with streaming
      const command = new ConverseStreamCommand(commandParams);

      const response = await bedrockRuntime.send(command);

      // Process the stream
      let fullResponse = '';
      let assistantContent = [];
      let stopReason = null;
      let currentTextBlock = '';
      
      if (response.stream) {
        for await (const event of response.stream) {
          if (event.contentBlockStart) {
            // New content block starting
            if (event.contentBlockStart.start?.toolUse) {
              // Tool use block starting
              const toolUse = event.contentBlockStart.start.toolUse;
              assistantContent.push({
                toolUse: {
                  toolUseId: toolUse.toolUseId,
                  name: toolUse.name,
                  input: {},
                },
              });
            }
          } else if (event.contentBlockDelta) {
            // Content block delta
            if (event.contentBlockDelta.delta?.text) {
              const deltaText = event.contentBlockDelta.delta.text;
              currentTextBlock += deltaText;
              fullResponse += deltaText;
              if (onChunk) {
                onChunk(deltaText);
              }
            } else if (event.contentBlockDelta.delta?.toolUse) {
              // Tool use input streaming
              const toolUseDelta = event.contentBlockDelta.delta.toolUse;
              // Accumulate tool input (it comes as a JSON string)
              const lastBlock = assistantContent[assistantContent.length - 1];
              if (lastBlock?.toolUse) {
                if (!lastBlock.toolUse.inputJson) {
                  lastBlock.toolUse.inputJson = '';
                }
                lastBlock.toolUse.inputJson += toolUseDelta.input || '';
              }
            }
          } else if (event.contentBlockStop) {
            // Content block completed
            if (currentTextBlock) {
              assistantContent.push({ text: currentTextBlock });
              currentTextBlock = '';
            }
            // Parse tool use input if present
            const lastBlock = assistantContent[assistantContent.length - 1];
            if (lastBlock?.toolUse?.inputJson) {
              try {
                lastBlock.toolUse.input = JSON.parse(lastBlock.toolUse.inputJson);
                delete lastBlock.toolUse.inputJson;
              } catch (e) {
                console.error('Error parsing tool input JSON:', e);
              }
            }
          } else if (event.messageStop) {
            // Message completed
            stopReason = event.messageStop.stopReason;
          }
        }
      }

      // Notify about tool use if present
      const toolUseBlocks = assistantContent.filter(block => block.toolUse);
      if (toolUseBlocks.length > 0 && onToolUse) {
        onToolUse(toolUseBlocks);
      }

      // Build conversation history based on whether we added a new user message
      const newHistory = message !== null ? [
        ...conversationHistory,
        {
          role: 'user',
          content: [{ text: message }],
        },
        {
          role: 'assistant',
          content: assistantContent,
        },
      ] : [
        ...conversationHistory,
        {
          role: 'assistant',
          content: assistantContent,
        },
      ];

      return {
        response: fullResponse,
        stopReason: stopReason,
        toolUse: toolUseBlocks.length > 0 ? toolUseBlocks : null,
        conversationHistory: newHistory,
        usage: null, // Stream API doesn't provide usage stats during streaming
      };
    } catch (error) {
      console.error('Error sending streaming message with tools to Bedrock:', error);
      throw error;
    }
  }

  /**
   * Send a message with streaming response
   * Always uses triple role assumption
   */
  async sendMessageStream(message, modelId = 'anthropic.claude-3-sonnet-20240229-v1:0', conversationHistory = [], onChunk) {
    try {
      // Always use assumed credentials
      const credentials = await this.getAssumedCredentials();

      // Create Bedrock Runtime client with the credentials
      const bedrockRuntime = new BedrockRuntimeClient({
        region: this.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      // Build the messages array from conversation history
      const messages = [
        ...conversationHistory,
        {
          role: 'user',
          content: modelId.includes('anthropic') ? [{ type: 'text', text: message }] : [{ text: message }],
        },
      ];

      // Prepare the request body
      const requestBody = {
        messages: messages,
      };

      // Add max_tokens and anthropic_version for Anthropic models
      if (modelId.includes('anthropic')) {
        requestBody.max_tokens = 4096;
        requestBody.anthropic_version = 'bedrock-2023-05-31';
      }

      // Invoke the model with streaming
      const command = new InvokeModelWithResponseStreamCommand({
        modelId: modelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(requestBody),
      });

      const response = await bedrockRuntime.send(command);

      // Process the stream
      let fullResponse = '';
      
      if (response.body) {
        for await (const event of response.body) {
          if (event.chunk) {
            const chunk = JSON.parse(new TextDecoder().decode(event.chunk.bytes));
            console.log('Streaming chunk:', chunk);
            
            // Handle different chunk types - process ALL text deltas
            if (chunk.contentBlockDelta?.delta?.text) {
              // Process all content blocks (the message may be split across multiple blocks)
              const deltaText = chunk.contentBlockDelta.delta.text;
              fullResponse += deltaText;
              if (onChunk) {
                onChunk(deltaText);
              }
            } else if (chunk.delta?.text && !chunk.contentBlockDelta) {
              // Fallback for different format
              const deltaText = chunk.delta.text;
              fullResponse += deltaText;
              if (onChunk) {
                onChunk(deltaText);
              }
            }
            // Ignore other chunk types (messageStart, contentBlockStop, messageStop, metadata)
          }
        }
      }

      return {
        response: fullResponse,
        conversationHistory: [
          ...conversationHistory,
          {
            role: 'user',
            content: [{ text: message }],
          },
          {
            role: 'assistant',
            content: [{ text: fullResponse }],
          },
        ],
      };
    } catch (error) {
      console.error('Error sending streaming message to Bedrock:', error);
      throw error;
    }
  }
}

export default new BedrockService();
